import { api } from './client'

/** `Domain/Contact.cs` fields, verified against the real C# entity (TASK-0171) — legacy
 * `ecrm_contacts` (pre-v3.5 style contact record). */
export interface Contact {
  id: number
  firstName: string
  lastName?: string
  companyName?: string
  email?: string
  phone?: string
  mobile?: string
  fiscalCode?: string
  vatNumber?: string
  status: string
  contactType?: string
  assignedAgentId?: number
  province?: string
  source?: string
  isCustomer: boolean
  customerId?: number
  createdAt: string
  mergedIntoContactId?: number | null
  mergedAt?: string | null
  /** F-095: quando il record e' stato spostato nel cestino (`null` = attivo). */
  deletedAt?: string | null
  deletedBy?: number | null
  deleteReason?: string | null
}

/** `GET /api/contacts/{id}` returns the full `Contact` entity (see `ContactsController.Get`,
 * backend), plus two extra fields present only when the record was merged away
 * (`canonicalContactId`/`canonicalResolution`, the live record at the end of the merge chain) —
 * same pattern as `CompanyDetail`. */
export interface ContactDetail extends Contact {
  canonicalContactId?: number
  canonicalResolution?: string
}

/** Mirrors `ContactRequest` (C#, `ContactsController.cs`). Deliberately no `forceCreate` — that
 * parameter is `Create`-only and this API client only ever calls `Update` (TASK-0171 scope). */
export interface ContactRequest {
  firstName: string
  lastName?: string
  email?: string
  phone?: string
  fiscalCode?: string
  province?: string
  source?: string
  assignedAgentId?: number
}

export const contactsApi = {
  get: (id: number) => api<ContactDetail>(`/api/contacts/${id}`),
  /** `Update` responds `409 Conflict` (not a generic HTTP error) when the contact is already
   * merged (`ContactsController.cs`, `contact.IsMerged`) — the body is `{ error, code,
   * mergedIntoContactId, canonicalContactId, canonicalResolution }`. The shared `api()` client
   * already turns any non-2xx response into a readable `Error` (via `err.error`), so no special
   * handling is needed here; the caller just needs to display that message (see
   * `ContactFormPage.tsx`). */
  update: (id: number, data: ContactRequest) =>
    api<void>(`/api/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
}

/** F-154 (TASK-0408, REQ-006-011): motivi ammessi di un blocco commerciale (elenco chiuso). */
export type CommercialBlockReason = 'Moroso' | 'Contestazione' | 'RichiestaDelCliente' | 'SospettaFrode' | 'Altro'

/** Un blocco commerciale di un contatto. Lo storico resta: un blocco tolto ha liftedAt. */
export interface CommercialBlock {
  id: number
  contactId: number
  reasonCode: CommercialBlockReason
  reasonLabel: string
  note?: string | null
  placedByUserId?: number | null
  placedAt: string
  liftedByUserId?: number | null
  liftedAt?: string | null
  isActive: boolean
}

export interface CommercialBlocksResponse {
  contactId: number
  hasActiveBlock: boolean
  reasons: { code: CommercialBlockReason; label: string }[]
  items: CommercialBlock[]
}

/** Lettura per lo staff, scrittura per Manager e Admin (l'agente riceve 403). Nota al massimo 500 caratteri. */
export const commercialBlocksApi = {
  list: (contactId: number) => api<CommercialBlocksResponse>(`/api/contacts/${contactId}/commercial-blocks`),
  place: (contactId: number, data: { reasonCode: CommercialBlockReason; note?: string | undefined }) =>
    api<CommercialBlock>(`/api/contacts/${contactId}/commercial-blocks`, { method: 'POST', body: JSON.stringify(data) }),
  lift: (contactId: number, blockId: number) =>
    api<CommercialBlock>(`/api/contacts/${contactId}/commercial-blocks/${blockId}/lift`, { method: 'POST' }),
}
